// Static service to replace Supabase functionality
// This service reads from local JSON files and serves static media
class StaticService {
  constructor() {
    this.API_BASE_URL =
      process.env.REACT_APP_API_URL || "http://localhost:8888";

    //Cache
    this._cache = {
      carouselData: null,
      teamMembers: null,
      newsItems: null,
      config: null,
    };

    // Cache timestamps to allow cache invalidation
    this._cacheTimestamps = {};
    this._cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Internal method to check if cache is valid
   */
  _isCacheValid(key) {
    if (!this._cache[key]) return false;
    const timestamp = this._cacheTimestamps[key];
    if (!timestamp) return false;
    return Date.now() - timestamp < this._cacheTimeout;
  }

  /**
   * Internal method to set cache
   */
  _setCache(key, data) {
    this._cache[key] = data;
    this._cacheTimestamps[key] = Date.now();
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this._cache = {
      carouselData: null,
      teamMembers: null,
      newsItems: null,
      config: null,
    };
    this._cacheTimestamps = {};
  }

  /**
   * Fetch data from a JSON endpoint with optional caching
   * @param {string} endpoint - The API endpoint to fetch from
   * @param {boolean} useCache - Whether to use cache (default: false)
   * @returns {Promise<Object>} Parsed JSON response
   */
  async fetchFromAPI(endpoint, useCache = false) {
    // Check cache if enabled
    if (useCache && this._isCacheValid(endpoint)) {
      console.log(`Using cached data for ${endpoint}`);
      return this._cache[endpoint];
    }

    try {
      const response = await fetch(`${this.API_BASE_URL}${endpoint}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store in cache if enabled
      if (useCache) {
        this._setCache(endpoint, data);
      }

      return data;
    } catch (error) {
      console.error(`Error fetching from ${endpoint}:`, error);
      throw error;
    }
  }

  /**
   * Validate QR code and optionally track usage
   * @param {string} qrId - QR code ID to validate
   * @param {boolean} trackUsage - Whether to increment usage counter
   * @returns {Promise<Object>} Validation result with QR code data
   */
  async validateQRCode(qrId, trackUsage = true) {
    try {
      console.log(`Validating QR code: ${qrId}`);

      // Fetch QR codes data from your Python backend
      const qrCodesData = await this.fetchFromAPI("/api/qr-codes");

      // Find the specific QR code (compare loosely)
      const qrCode = qrCodesData.codes.find(
        (code) => String(code.id) === String(qrId) && code.active === true,
      );

      if (!qrCode) {
        return {
          valid: false,
          error: "Invalid QR code",
          code: "INVALID_QR_CODE",
        };
      }

      // If we should track usage, send update request to backend
      /* Not tracking ---if (trackUsage) {
        try {
          await fetch(`${this.API_BASE_URL}/api/qr-codes/${qrId}/track`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              timestamp: new Date().toISOString(),
            }),
          });
        } catch (updateError) {
          console.warn("Failed to update QR code usage:", updateError);
          // Don't fail validation if usage tracking fails
        }
      }*/

      console.log(`QR code ${qrId} validated successfully:`, qrCode.name);

      return {
        valid: true,
        data: qrCode,
        message: `Welcome to ${qrCode.name}`,
      };
    } catch (error) {
      console.error("Unexpected error during QR validation:", error);
      return {
        valid: false,
        error: "System error during validation",
        code: "SYSTEM_ERROR",
      };
    }
  }

  /**
   * Fetch carousel data once with both groups and items
   * Returns structure: {
   *   groups: [{group_id, group_name, items: [...]}, ...],
   *   groupNames: ['group1', 'group2', ...],
   *   allItems: [...] // flattened if needed
   * }
   * @param {boolean} useCache - Whether to use cached data (default: true)
   * @returns {Promise<Object>} Complete carousel data
   */
  async fetchCarouselData(useCache = true) {
    try {
      // Check cache first
      if (useCache && this._isCacheValid("carouselData")) {
        console.log("Using cached carousel data");
        return this._cache.carouselData;
      }

      console.log("Fetching carousel data...");

      // Single API call for all carousel data
      const rawData = await this.fetchFromAPI("/api/carousel-items");

      if (!rawData.groups || rawData.groups.length === 0) {
        throw new Error("No carousel groups found");
      }

      // Process and enrich the data in one pass
      const processedData = {
        groups: rawData.groups,
        groupNames: rawData.groups.map((g) => g.group_name),
        allItems: rawData.groups.flatMap((g) =>
          g.items.map((item) => ({
            ...item,
            group_id: g.group_id,
            group_name: g.group_name,
          })),
        ),
        totalItems: rawData.groups.reduce((sum, g) => sum + g.items.length, 0),
      };

      console.log(
        `Carousel data loaded: ${processedData.groups.length} groups, ${processedData.totalItems} items`,
      );

      // Cache the processed data
      if (useCache) {
        this._setCache("carouselData", processedData);
      }

      return processedData;
    } catch (error) {
      console.error("Error fetching carousel data:", error);
      throw error;
    }
  }

  /**
   * Fetch all team members with caching
   * @param {boolean} useCache - Whether to use cached data (default: true)
   * @returns {Promise<Array>} Array of team member objects
   */
  async fetchTeamMembers(useCache = true) {
    try {
      // Check cache first
      if (useCache && this._isCacheValid("teamMembers")) {
        console.log("Using cached team members");
        return this._cache.teamMembers;
      }

      console.log("Fetching team members from backend...");

      const teamData = await this.fetchFromAPI("/api/team-members");
      const members = teamData || [];

      console.log(`Team members fetched: ${members.length} members`);

      // Cache the data
      if (useCache) {
        this._setCache("teamMembers", members);
      }

      return members;
    } catch (error) {
      console.error("Error fetching team members:", error);
      throw error;
    }
  }

  //Not used at the moment
  // /**
  //  * Fetch all "about" images from static files
  //  * @returns {Promise<Object[]>} Array of objects containing image data
  //  */
  // async getBrandImages() {
  //   try {
  //     console.log("Fetching brand images from static files...");

  //     // List of known filenames (add more here if needed)
  //     const fileNames = ["logo_white.png", "like.png"];

  //     const results = [];

  //     for (const fileName of fileNames) {
  //       const imageUrl = `${this.API_BASE_URL}/media/brands/${fileName}`;

  //       // Optional: check if the image exists
  //       try {
  //         const response = await fetch(imageUrl, { method: "HEAD" });
  //         if (!response.ok) {
  //           console.warn(`Image not found: ${fileName}`);
  //           continue; // skip missing files
  //         }
  //       } catch (checkError) {
  //         console.warn(`Error checking ${fileName}:`, checkError);
  //         continue;
  //       }

  //       results.push({
  //         name: fileName,
  //         url: imageUrl,
  //         metadata: null,
  //       });
  //     }

  //     return results;
  //   } catch (error) {
  //     console.error("Error fetching about images:", error);
  //     throw error;
  //   }
  // }

  //Not used at the moment
  // /**
  //  * Fetch Open Graph data for a given URL
  //  * @param {string} url - The URL to extract Open Graph data from
  //  * @returns {Promise<Object>} Open Graph data including title, description, image, etc.
  //  */
  // async fetchOpenGraphData(url) {
  //   console.log(`Fetching Open Graph data for URL: ${url}`);

  //   // Encode the URL parameter to handle special characters
  //   const encodedUrl = encodeURIComponent(url);
  //   const endpoint = `/api/open-graph?url=${encodedUrl}`;

  //   // Use the existing fetchFromAPI method to make the request
  //   const ogData = await this.fetchFromAPI(endpoint);

  //   console.log(
  //     `Open Graph data fetched successfully for: ${ogData.title || "Unknown"}`,
  //   );

  //   return ogData;
  // }

  /**
   * Fetch all news items with caching
   * @param {boolean} useCache - Whether to use cached data (default: true)
   * @returns {Promise<Array>} Array of news items
   */
  async fetchNewsItems(useCache = true) {
    try {
      // Check cache first
      if (useCache && this._isCacheValid("newsItems")) {
        console.log("Using cached news items");
        return this._cache.newsItems;
      }

      console.log("Fetching news items...");

      const newsData = await this.fetchFromAPI("/api/news");
      const items = newsData || [];

      console.log(`News items fetched: ${items.length} items`);

      // Cache the data
      if (useCache) {
        this._setCache("newsItems", items);
      }

      return items;
    } catch (error) {
      console.error("Error fetching news items:", error);
      throw error;
    }
  }

  //Not used at the moment
  // /**
  //  * Fetch all instagram posts
  //  * @returns {Promise<Array>} Array of instagram posts
  //  */
  // async fetchInstagramPosts() {
  //   try {
  //     const instagramData = await this.fetchFromAPI("/api/instagram");
  //     return instagramData || [];
  //   } catch (error) {
  //     console.error("Error fetching Instagram posts:", error);
  //     throw error;
  //   }
  // }

  //Not used at the moment
  // /**
  //  * Fetch the latest IMAGE_ON_AIR image
  //  * @returns {Promise<Object>} Latest shared image data
  //  */
  // async fetchLatestImageOnAir() {
  //   try {
  //     const response = await this.fetchFromAPI("/api/image_on_air/latest");
  //     return response;
  //   } catch (error) {
  //     console.error("Error fetching latest IMAGE_ON_AIR:", error);
  //     throw error;
  //   }
  // }

  /**
   * Fetch frontend configuration with caching
   * @param {boolean} useCache - Whether to use cached data (default: true)
   * @returns {Promise<Object>} Configuration object
   */
  async fetchConfig(useCache = true) {
    try {
      // Check cache first
      if (useCache && this._isCacheValid("config")) {
        console.log("Using cached config");
        return this._cache.config;
      }

      const config = await this.fetchFromAPI("/api/config");

      // Cache the data
      if (useCache) {
        this._setCache("config", config);
      }

      return config;
    } catch (error) {
      console.error("Error fetching config:", error);
      throw error;
    }
  }

  /**
   * Batch fetch all initial app data in parallel
   * Returns all data needed for app startup in one call
   * @returns {Promise<Object>} Object containing all preloaded data
   */
  async batchFetchAppData() {
    console.log("Batch fetching all app data...");

    const startTime = performance.now();

    try {
      // Fetch all data in parallel for maximum speed
      const [carouselData, teamMembers, newsItems] = await Promise.allSettled([
        this.fetchCarouselData(true),
        this.fetchTeamMembers(true),
        this.fetchNewsItems(true),
      ]);

      const endTime = performance.now();
      console.log(
        `Batch fetch completed in ${(endTime - startTime).toFixed(2)}ms`,
      );

      // Process results and errors
      const result = {
        carouselData:
          carouselData.status === "fulfilled" ? carouselData.value : null,
        teamMembers:
          teamMembers.status === "fulfilled" ? teamMembers.value : [],
        newsItems: newsItems.status === "fulfilled" ? newsItems.value : [],
        errors: {
          carousel:
            carouselData.status === "rejected"
              ? carouselData.reason.message
              : null,
          teamMembers:
            teamMembers.status === "rejected"
              ? teamMembers.reason.message
              : null,
          newsItems:
            newsItems.status === "rejected" ? newsItems.reason.message : null,
        },
      };

      console.log("Batch fetch results:", {
        carouselGroups: result.carouselData?.groups?.length || 0,
        carouselItems: result.carouselData?.totalItems || 0,
        teamMembers: result.teamMembers?.length || 0,
        newsItems: result.newsItems?.length || 0,
        errors: Object.values(result.errors).filter(Boolean).length,
      });

      return result;
    } catch (error) {
      console.error("Unexpected error during batch fetch:", error);
      throw error;
    }
  }
}

// Export a singleton instance
const staticService = new StaticService();

export default staticService;
